import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Copy, Check, Wand2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: 'curso', label: 'Descrição de Curso/Formação' },
  { value: 'email', label: 'E-mail de Marketing' },
  { value: 'post', label: 'Post para Redes Sociais' },
  { value: 'generico', label: 'Texto Genérico' },
];

const TONES = [
  { value: 'profissional', label: 'Profissional' },
  { value: 'amigavel', label: 'Amigável' },
  { value: 'persuasivo', label: 'Persuasivo' },
  { value: 'inspirador', label: 'Inspirador' },
];

const AdminContentGenerator = () => {
  const [contentType, setContentType] = useState('curso');
  const [tone, setTone] = useState('profissional');
  const [brief, setBrief] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!brief.trim()) {
      toast.error('Descreva o que deseja gerar');
      return;
    }
    setLoading(true);
    setResult('');
    try {
      const res = await axios.post(
        `${API}/assistant/admin/generate`,
        { content_type: contentType, brief, tone },
        { withCredentials: true }
      );
      setResult(res.data.text || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao gerar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error('Não foi possível copiar');
    }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Sparkles className="text-red-500" size={22} />
          Gerador de Conteúdo com IA (ChatGPT)
        </CardTitle>
        <p className="text-sm text-gray-400">
          Gere descrições de cursos, e-mails e posts para redes sociais em segundos.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Tipo de conteúdo</label>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              data-testid="content-type-select"
              className="w-full bg-zinc-800 text-white rounded-lg px-3 py-2.5 border border-zinc-700 focus:outline-none focus:border-red-500"
            >
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">Tom</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              data-testid="content-tone-select"
              className="w-full bg-zinc-800 text-white rounded-lg px-3 py-2.5 border border-zinc-700 focus:outline-none focus:border-red-500"
            >
              {TONES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1.5">Briefing / instruções</label>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={4}
            placeholder="Ex.: Descrição para a Formação Revit Arquitetura, destacando modelagem, documentação e certificado, público iniciante."
            data-testid="content-brief-input"
            className="w-full bg-zinc-800 text-white text-sm rounded-lg px-3 py-2.5 border border-zinc-700 focus:outline-none focus:border-red-500 placeholder:text-gray-500 resize-y"
          />
        </div>

        <Button
          onClick={generate}
          disabled={loading}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="content-generate-btn"
        >
          {loading ? (
            <><Loader2 className="mr-2 animate-spin" size={18} /> Gerando...</>
          ) : (
            <><Wand2 className="mr-2" size={18} /> Gerar Conteúdo</>
          )}
        </Button>

        {result && (
          <div className="bg-zinc-950/60 border border-zinc-700 rounded-lg p-4" data-testid="content-result">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Resultado</span>
              <Button
                onClick={copy}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-gray-300 hover:bg-zinc-800 h-8"
                data-testid="content-copy-btn"
              >
                {copied ? <><Check size={14} className="mr-1 text-green-400" /> Copiado</> : <><Copy size={14} className="mr-1" /> Copiar</>}
              </Button>
            </div>
            <div className="text-gray-100 text-sm whitespace-pre-wrap leading-relaxed">{result}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminContentGenerator;
